import { BLOCKS, BODIES, HAIRS, HATS, sanitizeLook, type Look } from "@dyson/shared";
import { coarsePointer, goFullscreen } from "@massivewebgames/screen";
import { Game } from "./game/Game";
import { CrewAnimator, crewModel, crewPalette, crewTraits } from "./render/rig/crew";
import { css } from "./render/rig/Rig";
import { drawRig } from "./render/rig/RigCanvas";
import { atlasCanvas, TILE_PX, tileUv } from "./render/TileAtlas";
import { PasswordRequired, saveCharacter, setAccessPassword, signOut, startSession, type Session } from "./net/session";
import { loadLayout } from "./net/layout";

const canvas = document.getElementById("game") as HTMLCanvasElement;
const joinOverlay = document.getElementById("join")!;
const form = document.getElementById("join-form") as HTMLFormElement;
const nameInput = document.getElementById("join-name") as HTMLInputElement;
const joinError = document.getElementById("join-error")!;
const passwordInput = document.getElementById("join-password") as HTMLInputElement;
const hueInput = document.getElementById("look-hue") as HTMLInputElement;
const hairSelect = document.getElementById("look-hair") as HTMLSelectElement;
const hatSelect = document.getElementById("look-hat") as HTMLSelectElement;
const bodySelect = document.getElementById("look-body") as HTMLSelectElement;
const preview = document.getElementById("look-preview") as HTMLCanvasElement;
/** Pixels the preview is drawn at, a figure metre: the game's own pixel art, a pixel to a pixel. */
const PREVIEW_PPU = 2 * TILE_PX;
/** The preview figure, idling; and whether it's being drawn (only while the join screen is up). */
const idle = new CrewAnimator(0.3);
let previewing = false;
let lastFrame = 0;

// The game server: in development the Node server (or wrangler dev) on 8787; deployed, the Worker serving this page.
const serverUrl: string = import.meta.env.VITE_SERVER_URL ?? (import.meta.env.DEV ? `http://${location.hostname}:8787` : location.origin);

const game = new Game(canvas, serverUrl, serverUrl.replace(/^http/, "ws"));
// In development the game is reachable from the console (and scripts driving a headless browser), to look inside.
if (import.meta.env.DEV) (window as unknown as { game: Game }).game = game;

const account = document.getElementById("account")!;
const accountText = document.getElementById("account-text")!;
const accountAction = document.getElementById("account-action") as HTMLButtonElement;

/** Where logins exist: who you are, and the button to save your character (or sign out). */
function showAccount(session: Session): void {
  account.classList.toggle("hidden", !session.accounts);
  if (session.user) {
    accountText.textContent = `Signed in as ${session.user.name}.`;
    accountAction.textContent = "Sign out";
    accountAction.onclick = () => void signOut(serverUrl).then(() => location.reload());
  } else {
    accountText.textContent = "Playing as a guest: your character lives only in this browser.";
    accountAction.textContent = "Save your character";
    accountAction.onclick = () => void saveCharacter(serverUrl).catch((err) => (joinError.textContent = (err as Error).message));
  }
}
game.onSession = showAccount;
// Show it on the join screen straight away (and after coming back from signing in).
void startSession(serverUrl).then(
  (session) => {
    showAccount(session);
    // Fetch the world's layout while they're still on the join screen.
    void loadLayout(serverUrl, session.sphere);
  },
  (err) => {
    if (err instanceof PasswordRequired) askPassword(err.message);
  },
);

/** The server wants its password before anyone plays: show the box for it. */
function askPassword(message: string): void {
  passwordInput.classList.remove("hidden");
  passwordInput.value = "";
  joinError.textContent = message;
}
if (new URLSearchParams(location.search).has("signedin")) history.replaceState(null, "", location.pathname);

nameInput.value = localStorage.getItem("dyson.name") ?? "";
nameInput.focus();

const HAIR_NAMES: Record<Look["hair"], string> = { crop: "Cropped", tail: "Ponytail", bun: "Bun" };
for (const hair of HAIRS) hairSelect.append(new Option(HAIR_NAMES[hair], hair));
for (const hat of HATS) hatSelect.append(new Option(hat === "none" ? "No hat" : capitalise(hat), hat));
for (const body of BODIES) bodySelect.append(new Option(capitalise(body), body));

const look = loadLook();
hueInput.value = String(look.hue);
hairSelect.value = look.hair;
hatSelect.value = look.hat;
bodySelect.value = look.body;
// The preview follows every change (the name too: it picks skin and hair colour); the look is saved when you launch.
showPreview();

// The coat slider's track, in the coats you'd get along it.
hueInput.style.background = `linear-gradient(to right, ${Array.from({ length: 13 }, (_, i) => css(crewPalette(i / 12, crewTraits("")).coat!)).join(", ")})`;

form.addEventListener("submit", (e) => {
  e.preventDefault();
  const name = nameInput.value.trim() || "Captain";
  const look = currentLook();
  localStorage.setItem("dyson.name", name);
  localStorage.setItem("dyson.look", JSON.stringify(look));
  if (!passwordInput.classList.contains("hidden")) setAccessPassword(passwordInput.value);
  joinError.textContent = "";
  joinOverlay.classList.add("hidden");
  // On a phone the browser's own bars take a strip of the screen: ask for full
  // screen while we still hold the tap that launched the game, since a browser
  // only grants it from a gesture (an iPhone never does; the rest of the layout
  // allows for that). The ⛶ button in the touch controls puts it back.
  if (coarsePointer()) void goFullscreen();
  game.join(name, look);
});

game.onError = (message, password) => {
  joinOverlay.classList.remove("hidden");
  showPreview();
  if (password) askPassword(message);
  else joinError.textContent = message;
};

/** The saved look, or a random coat colour and hair for a first visit (so not everyone turns up the same). */
function loadLook(): Look {
  try {
    const saved = sanitizeLook(JSON.parse(localStorage.getItem("dyson.look") ?? "null"));
    if (saved) return saved;
  } catch {
    // A garbled save: start over.
  }
  return { hue: Math.floor(Math.random() * 360), hat: "none", body: "standard", hair: HAIRS[Math.floor(Math.random() * HAIRS.length)]! };
}

function currentLook(): Look {
  return sanitizeLook({ hue: Number(hueInput.value), hat: hatSelect.value, body: bodySelect.value, hair: hairSelect.value })!;
}

function capitalise(s: string): string {
  return s[0]!.toUpperCase() + s.slice(1);
}

/** The name the game will know you by (so the skin and hair it picks match the preview's). */
const playerName = () => nameInput.value.trim() || "Captain";

/** Keep the preview going while the join screen is up. */
function showPreview(): void {
  if (previewing) return;
  previewing = true;
  lastFrame = performance.now();
  requestAnimationFrame(previewFrame);
}

function previewFrame(now: number): void {
  if (joinOverlay.classList.contains("hidden")) {
    previewing = false;
    return;
  }
  const dt = Math.min(0.1, (now - lastFrame) / 1000);
  lastFrame = now;
  drawPreview(currentLook(), dt);
  requestAnimationFrame(previewFrame);
}

/**
 * You as the game will draw you: the crew rig in the chosen coat, hair, hat
 * and build, with the skin and hair colour your name gives you, idling on a
 * strip of the game's own grass.
 */
function drawPreview(look: Look, dt: number): void {
  const g = preview.getContext("2d")!;
  const W = preview.width;
  const H = preview.height;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.imageSmoothingEnabled = false;
  g.clearRect(0, 0, W, H);
  const ground = H - 10;
  const { u0, v1 } = tileUv(BLOCKS.GRASS.id, 0);
  for (let x = 0; x < W; x += TILE_PX) g.drawImage(atlasCanvas, Math.round(u0 * atlasCanvas.width), Math.round((1 - v1) * atlasCanvas.height), TILE_PX, 10, x, ground, TILE_PX, 10);
  const traits = crewTraits(playerName());
  idle.build = look.body;
  const pose = idle.step(dt, { mode: "ground", facing: 1, along: 0, rise: 0, grounded: true, aim: null, tool: null, using: false });
  // Feet on the grass, as they'll start: nothing on their back yet.
  g.setTransform(PREVIEW_PPU, 0, 0, -PREVIEW_PPU, Math.round(W * 0.56), ground);
  drawRig(g, crewModel(look.hair, look.hat, null, look.body, false), pose, crewPalette(look.hue / 360, traits), 1);
}
